/**
 * Iter145 — Admin editor for coach-assigned sports.
 *
 * Two-tile picker on the AdminUserDetail page. Only shown when the target
 * user has role `coach` or `admin`. Persists via PUT /admin/users/{id}/coach-sports.
 */
import { useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SPORTS = [
  { key: "squash", label: "Squash", color: "#CCFF00" },
  { key: "pickleball", label: "Pickleball", color: "#FF4500" },
];

export default function CoachSportsEditor({ userId, initial, onSaved }) {
  const [selected, setSelected] = useState(() => Array.isArray(initial) ? initial : []);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => {
    setSelected((cur) => cur.includes(k) ? cur.filter((s) => s !== k) : [...cur, k]);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/users/${userId}/coach-sports`, { coach_sports: selected });
      toast.success(`Sports updated: ${(data?.coach_sports || []).join(", ") || "none"}`);
      if (onSaved) onSaved(data?.coach_sports || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...(initial || [])].sort());
  return (
    <div className="card-surface p-6 space-y-4" data-testid="coach-sports-editor">
      <div className="label-mono text-[#CCFF00]">COACH SPORTS</div>
      <div className="text-sm text-[#A3A3A3]">
        Tick the sports this coach is allowed to publish. Untick to hide them
        from the sport picker on their offering form.
      </div>
      <div className="grid grid-cols-2 gap-2">
        {SPORTS.map((s) => {
          const on = selected.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              data-testid={`coach-sports-${s.key}`}
              className="py-3 text-sm uppercase tracking-wider border transition-colors"
              style={on
                ? { background: s.color, color: "#0A0A0A", borderColor: s.color, fontWeight: 700 }
                : { borderColor: "#262626", color: "#A3A3A3" }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={save}
        disabled={!dirty || saving}
        data-testid="coach-sports-save"
        className="btn-volt py-2 px-4 text-xs inline-flex items-center gap-2 disabled:opacity-40"
      >
        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save sports"}
      </button>
    </div>
  );
}
